import Banner from '@/components/UI/Banner';
import CopyToClipboard from '@/components/UI/CopyToClipboard';
import { successToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import { EntitySchemaApiId, EntitySchemaV1 } from '@/model/EntitySchema.model';
import { useEntitySchemaContext } from '@/utils/entitySchema/EntitySchemaContext';
import { useTableEditState } from './useTableEditState';
import { TableActionButtons } from './TableActionButtons';
import { RequiredCheckboxCell } from './RequiredCheckboxCell';
import { ExpandableIcon } from './ExpandableIcon';
import { FC, Fragment, useState } from 'react';
import { TABLE_CLASSES } from './IndividualSaveRepeater.constants';
import './IndividualSaveRepeater.scss';

interface EntitySchemaTableField {
  id: EntitySchemaApiId;
  title: string;
  field_type: string;
  slug: string;
  required: boolean;
  editable: boolean;
  expandable: boolean;
  expanded?: boolean;
}

const EntitySchemaTable: FC = () => {
  const { entityFields, entityLoading, entityUpdating, setFieldRequired } = useEntitySchemaContext();
  const {
    tempValues: tempRequired,
    isEditing,
    startEdit,
    cancelEdit,
    saveEdit,
    updateTempValue,
  } = useTableEditState<boolean>();
  const [expandedItems, setExpandedItems] = useState<Set<ID>>(new Set());

  const transformedFields: EntitySchemaTableField[] = entityFields.map((field: EntitySchemaV1) => ({
    id: field.api_id,
    title: field.field_name,
    field_type: field.field_type,
    slug: field.slug,
    required: field.required,
    editable: field.editable,
    expandable: field.slug === 'sex',
    expanded: expandedItems.has(field.api_id),
  }));

  const handleEditClick = (itemId: string) => {
    if (isEditing(itemId)) {
      cancelEdit(itemId);
    } else {
      const field = entityFields.find((f) => f.api_id === itemId);
      if (_notNil(field)) {
        startEdit(itemId, field.required);
      }
    }
  };

  const handleSave = async (itemId: EntitySchemaApiId) => {
    const field = entityFields.find((f) => f.api_id === itemId);
    const fieldName = field?.field_name || 'Field';

    await saveEdit(itemId, (required: boolean) => setFieldRequired(itemId, required));

    successToast(`${fieldName} updated successfully`);
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const sexOptions = ['male', 'female'];

  if (entityLoading) {
    return null;
  }

  return (
    <div data-testid="entity-schema-table">
      <h3 className="f5 normal basier-med lh-title mb1 mt4" data-testid="entity-schema-title">
        System fields
      </h3>
      <p className="f6 mt1" data-testid="entity-schema-description">
        These fields are provided by default and cannot be archived. Some can be set as required or optional.
      </p>

      {_isEmpty(transformedFields) ? (
        <div className="mv4" data-testid="entity-schema-empty-state">
          <Banner info className="mw6" dismiss={false}>
            <h3 className="f5 normal basier-med lh-title" data-testid="empty-state-title">
              No system fields available
            </h3>
            <p className="f6 pt2 lh-copy" data-testid="empty-state-message">
              System fields are not configured for this team.
            </p>
          </Banner>
        </div>
      ) : (
        <div
          className="ui-card overflow-hidden ui__table ui__repeater bb b--moon-gray mt4"
          data-testid="entity-schema-table-container"
        >
          <table className="w-100 tl bt b--moon-gray individual-save-table" data-testid="entity-schema-fields-table">
            <thead className="bg-near-white">
              <tr>
                <th className={TABLE_CLASSES.TH}></th>
                <th className={TABLE_CLASSES.TH}>Field name</th>
                <th className={TABLE_CLASSES.TH}>Field type</th>
                <th className={TABLE_CLASSES.TH}>Slug</th>
                <th className={TABLE_CLASSES.TH}>Required</th>
                <th className={TABLE_CLASSES.TH}></th>
              </tr>
            </thead>
            <tbody className="black">
              {transformedFields.map((field) => {
                const isFieldEditing = isEditing(field.id);
                const isSaving = entityUpdating;

                return (
                  <Fragment key={field.id}>
                    <tr className={`${isSaving ? 'o-50' : ''}`} data-testid={`entity-field-row-${field.slug}`}>
                      <td className={`${TABLE_CLASSES.TD} pv2 pv3-l`} data-testid={`expand-cell-${field.slug}`}>
                        <ExpandableIcon
                          itemId={field.id}
                          isExpandable={field.expandable}
                          isExpanded={field.expanded}
                          onToggle={(itemId) => toggleExpanded(itemId as string)}
                          testIdPrefix={field.slug}
                        />
                      </td>

                      <td className={`${TABLE_CLASSES.TD} pv2 pv3-l`} data-testid={`field-name-${field.slug}`}>
                        <span className="f7 f6-l">{field.title}</span>
                      </td>

                      <td className={`${TABLE_CLASSES.TD} pv2 pv3-l`} data-testid={`field-type-${field.slug}`}>
                        <span>{field.field_type}</span>
                      </td>

                      <td className={`${TABLE_CLASSES.TD} pv2 pv3-l`} data-testid={`field-slug-${field.slug}`}>
                        {_notNil(field.slug) && (
                          <CopyToClipboard textToCopy={field.slug} toastText="Slug copied to clipboard">
                            <span>{field.slug}</span>
                          </CopyToClipboard>
                        )}
                      </td>

                      <td className={`${TABLE_CLASSES.TD} pv2 pv3-l`} data-testid={`field-required-${field.slug}`}>
                        <RequiredCheckboxCell
                          itemId={field.id}
                          isEditing={isFieldEditing}
                          isEditable={field.editable}
                          required={field.required}
                          tempRequired={tempRequired[field.id]}
                          isSaving={isSaving}
                          onChange={(itemId, checked) => updateTempValue(itemId, checked)}
                          testIdPrefix={field.slug}
                        />
                      </td>

                      <td className={`${TABLE_CLASSES.TD} v-mid pv2 pv3-l`} data-testid={`field-actions-${field.slug}`}>
                        <div className="flex items-center justify-end">
                          <TableActionButtons
                            itemId={field.id}
                            isEditing={isFieldEditing}
                            isEditable={field.editable}
                            isSaving={isSaving}
                            onEdit={(itemId) => handleEditClick(itemId as string)}
                            onSave={(itemId) => handleSave(itemId as EntitySchemaApiId)}
                            onCancel={(itemId) => handleEditClick(itemId as string)}
                            testIdPrefix={field.slug}
                          />
                          <div className="ml3 spacer-26"></div>
                        </div>
                      </td>
                    </tr>

                    {field.expandable && field.expanded && (
                      <tr data-testid={`expanded-options-${field.slug}`}>
                        <td colSpan={6}>
                          <div
                            className="pv3 pr3 bt b--moon-gray expanded-panel-padding"
                            data-testid={`options-container-${field.slug}`}
                          >
                            <h4 className="f6 ma0 mb3" data-testid={`options-title-${field.slug}`}>
                              Available Options:
                            </h4>
                            <div className="flex flex-wrap" data-testid={`options-list-${field.slug}`}>
                              {sexOptions.map((option, index) => (
                                <div
                                  key={index}
                                  className="bg-moon-gray ba b--gray br2 ph2 pv1 f6 near-black flex items-center justify-center mr2 mb2"
                                  data-testid={`option-chip-${option}`}
                                >
                                  <span>{option}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntitySchemaTable;
